

import React, { createRef } from 'react';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import CachedImg from 'components/common/CachedImg/CachedImg';

import {
    AudioPlayerPreview,
    BackIcon,
    CategoryChipButton,
    CategoryChipList,
    ChannelNumber,
    ChannelTitle,
    ChevronLeftIcon,
    Container,
    ControlPillButton,
    ErrorState,
    ErrorStateText,
    FullScreenContainer,
    HiddenPlayerMount,
    LiveBadge,
    LoadingIndicator,
    LoadingSpinner,
    LoadingSpinnerArc,
    LoadingText,
    LogoDisabledIcon,
    LogoIcon,
    NavHeader,
    NavHeaderTitle,
    NewsTickerContainer,
    NewsTickerIcon,
    NewsTickerItem,
    NewsTickerSeparator,
    NewsTickerText,
    NewsTickerTrack,
    NewsTickerViewport,
    RadioCardFrequency,
    RadioCardLogoBox,
    RadioCardMeta,
    RadioCardTitle,
    RadioIcon,
    SidebarChannelList,
    SidebarChannelLogoBox,
    SidebarChannelRow,
    SidebarPanel,
    SignalIcon,
    SpeakerIcon,
    StationCard,
    StationFrequency,
    StationTitle,
    StreamLoadingOverlay,
    TopRightControls,
    TvIcon,
    TvWrapper,
    VideoBackground,
} from './styles';
import { VideoPlayerArea } from './components/TVChannelsList/styles';

import BaseChannelTemplate from 'components/channels/BaseChannelTemplate';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { updatePlayerState, getPlayerState } from 'components/channels/common/channelPlayerState';
import { measureComponent } from 'utils/common/measureComponent';
import { scrollChildIntoView } from 'utils/dom';
import { translate as t } from 'utils/i18n';
import { ContentContainer } from 'design-tokens/warwick/common';

import getLocalizedNews from 'components/news/getLanguageData';
import { fetchRssFeed } from '../common/utils';
import { parseRssData } from 'utils/rss/parseRss';
import EventBus from 'utils/eventBus';
import { isValidUrl } from 'utils/validations/url_validation';

/**
 * WarwickTvNews (standalone) — Warwick TV template + news ticker,
 * built directly on BaseChannelTemplate.
 *
 * Functionally identical to the WarwickTv-inherited version, but every
 * code path that only existed for the retired "bottom guide" layout
 * (toggleLayout, NAVIGATION_MODES.BOTTOM, renderBottomChannelList, the
 * horizontal channel-list nav branches, the unused Bottom* styled
 * components) has been removed rather than left unused — so none of it
 * ships in the bundle for builds that only need the sidebar+news variant
 * and never load WarwickTv itself.
 */

const ALL_CATEGORIES_LABEL = 'All';

const SECTION = {
    CATEGORIES: 'categories',
    CHANNEL_LIST_VERT: 'channelListVert',
    ACTION_BUTTONS: 'actionButtons',
};

const ACTION_ID = {
    TOGGLE_LOGOS: 'toggleLogos',
}; 

class WarwickTvNews extends BaseChannelTemplate {
    static defaultProps = {
        isRadio: false,
    };

    constructor(props) {
        super(props);
        this.controlsHideTimer = null;
        this.sleepTime = 5 * 1000; // 5 sec
        this.wallpaperImage = null;
        this.activeActionRef = createRef();
        this.activeCategoryRef = createRef();
        this.activeChannelRef = createRef();

        this.state = Object.assign({}, this.state, {
            focusedSection: SECTION.CATEGORIES,
            focusIndex: null,
            focusedActionId: ACTION_ID.TOGGLE_LOGOS,
            showLogos: true,
            playerWasFullscreen: false,
            highlightedChannelIndex: 0,

            headlines: [],

            // Controls are visible initially
            slideList: true,

            categories: [],
            selectedCategory: ALL_CATEGORIES_LABEL,
            categoryFocusIndex: 0,
            categoryDisplayList: [],
            filteredItems: [],
        });
    }

    componentDidMount() {
        super.componentDidMount();
        var deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);
        if (this.isRadioMode()) {
            document.documentElement.classList.remove('tep-preview-active');
            document.body.classList.remove('tep-preview-active');
            // Radio has no preview pane — use native fullscreen so TEP skips preview coords.
            updatePlayerState({ displayFullScreen: true });
        } else if (deviceList && deviceList.isTIZEN_TEP) {
            document.documentElement.classList.remove('tep-preview-active');
            document.body.classList.remove('tep-preview-active');
            updatePlayerState({ displayFullScreen: false });
            this.enterFullscreen(false);
        } else {
            this.enterFullscreen(false);
        }

        this.refreshSubscription = EventBus.subscribe('refresh', () => this.getData())
    }

    componentDidUpdate(prevProps, prevState) {
        super.componentDidUpdate(prevProps, prevState);

        const { focusedSection: previousSection } = prevState;
        const { focusedSection: currentSection } = this.state;
        const { ACTION_BUTTONS } = SECTION;

        // Section changes
        if (previousSection !== currentSection) {
            if ([previousSection, currentSection].includes(ACTION_BUTTONS)) {
                if (previousSection === ACTION_BUTTONS) {
                    this.activeActionRef?.current?.blur();
                } else if (currentSection === ACTION_BUTTONS) {
                    this.activeActionRef?.current?.focus();
                }
            }
        }

        // Channel highlight changed
        if (prevState.highlightedChannelIndex !== this.state.highlightedChannelIndex) {
            this.scrollHighlightedChannelIntoView();
        }

        // Channel changed but highlight not changed
        if (prevState.focusIndex !== this.state.focusIndex && this.state.focusIndex !== this.state.highlightedChannelIndex) {
            this.setState({ highlightedChannelIndex: this.state.focusIndex });
        }

        // Category highlight changed
        if (
            prevState.categoryFocusIndex !== this.state.categoryFocusIndex ||
            (prevState.focusedSection !== this.state.focusedSection && this.state.focusedSection === SECTION.CATEGORIES)
        ) {
            this.scrollActiveCategoryIntoView();
        }

        // Items loaded
        if (prevState.items !== this.state.items && this.state.items && this.state.items.length > 0) {
            if (!this.state.filteredItems || this.state.filteredItems.length === 0) {
                this.applyFilteredItems(this.state.items, (filteredItems) => {
                    this.syncFocusToFilteredItems(filteredItems);
                });
            }
        }

        // Fullscreen -> minimized
        const wasFullscreen = prevState.playerWasFullscreen;
        const isFullscreen = this.isFullscreen();

        if (wasFullscreen !== undefined && wasFullscreen && !isFullscreen) {
            if (!this.state.slideList) {
                this.setState({ slideList: true });
            }
        }

        if (wasFullscreen !== isFullscreen) {
            this.setState({ playerWasFullscreen: isFullscreen });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.controlsHideTimer);
        super.componentWillUnmount();
        this.refreshSubscription.unsubscribe()
    }




    // ---------------------------------------------------------------------------
    // procccess feed urls
    // ---------------------------------------------------------------------------
    async processFeedUrls(urls) {
        if (!urls || !urls.length) return;

        const items = [];
        for (let i = 0; i < urls.length; i++) {
            const headlines = await this.fetchAndParseFeed(urls[i]);
            if (!headlines || !headlines.length) continue;
            for (let j = 0; j < headlines.length; j++) {
                items.push(headlines[j]);
            }
        }

        if (this._unmounted || items.length === 0) return;

        this.setState({ headlines: items });
    }
    // ---------------------------------------------------------------------------
    // fetch xml data and parse into useable strings
    // ---------------------------------------------------------------------------
    async fetchAndParseFeed(url) {
        if (!url) return [];

        try {
            const data = await fetchRssFeed(url);
            const items = parseRssData(data);
            const headlines = [];
            for (let i = 0; i < items.length; i++) {
                const text = items[i].title || items[i].description;
                if (text) headlines.push(text);
            }
            return headlines;
        } catch (error) {
            console.error('[RSS-PARSE-ERROR]', url, error);
            return [];
        }
    }
    // ---------------------------------------------------------------------------
    // News loading
    // ---------------------------------------------------------------------------
    getNewsData() {
        const newsData = getLocalizedNews();
        const rssFeedsUrls = [];

        (newsData?.languageData ?? [])
            .forEach((newsItem) => {
                const locale_description = newsItem?.locale_latest_news?.locale_description;
                if (locale_description && isValidUrl(locale_description)) {
                    rssFeedsUrls.push(locale_description);
                }
            });

        // for processing only first rss feed 
        this.processFeedUrls(rssFeedsUrls.slice(0, 1));
    }



    // ---------------------------------------------------------------------------
    // Data loading
    // ---------------------------------------------------------------------------

    isRadioMode() {
        return Boolean(this.props.isRadio);
    }

    getModeItems(items = this.state.items) {
        return this.channelData.filterChannelsByMode(items, this.isRadioMode());
    }

    syncFocusToFilteredItems(filteredItems) {
        if (!filteredItems.length) return;

        const { focusIndex } = this.state;
        if (focusIndex == null || focusIndex < 0 || focusIndex >= filteredItems.length) {
            this.changeChannelIndex(0);
            return;
        }

        this.changeChannelIndex(focusIndex);
    }

    applyFilteredItems(items, callback) {
        const filteredItems = this.getFilteredItems(items);

        this.setState(
            {
                filteredItems,
                selectedCategory: ALL_CATEGORIES_LABEL,
                highlightedChannelIndex: 0,
            },
            () => {
                this.deriveCategories();
                if (typeof callback === 'function') {
                    callback(filteredItems);
                }
            }
        );
    }

    async getData() {
        await super.getData();
        this.getNewsData();
        const modeItems = this.getModeItems();

        if (!modeItems.length) {
            if (this.isRadioMode()) {
                await this.stopPlayer();
            }
            this.setState({
                filteredItems: [],
                categories: [ALL_CATEGORIES_LABEL],
                categoryDisplayList: [{ label: ALL_CATEGORIES_LABEL, active: true }],
                selectedCategory: ALL_CATEGORIES_LABEL,
            });
            return;
        }

        this.applyFilteredItems(this.state.items, (filteredItems) => {
            this.syncFocusToFilteredItems(filteredItems);
        });
    }

    async deriveCategories() {
        const modeItems = this.getModeItems();

        if (!modeItems.length) {
            this.setState({
                categories: [ALL_CATEGORIES_LABEL],
                categoryDisplayList: [{ label: ALL_CATEGORIES_LABEL, active: true }],
                categoryFocusIndex: 0,
            });
            return;
        }

        var types = modeItems.map((ch) => ch.channel_type).filter(Boolean);
        var unique = types.filter((value, index, self) => self.indexOf(value) === index);
        var categories = [ALL_CATEGORIES_LABEL].concat(unique);

        const categoryDisplayList = this.buildCategoryDisplayList(0, categories);

        this.setState({ categories, categoryDisplayList, categoryFocusIndex: 0 });
    }

    // ---------------------------------------------------------------------------
    // Measurement
    // ---------------------------------------------------------------------------

    blankComponnet() {
        return null;
    }

    getMeasurements(sampleItem) {
        return measureComponent(() => this.blankComponnet, {
            useableDimension: 0.6,
            width: false,
            ensureOdd: false,
            extraSpace: 16,
        });
    }

    // ---------------------------------------------------------------------------
    // Channel list generation
    // ---------------------------------------------------------------------------

    async generateNewChannelList(newIndex, list) {
        if (!list || list.length === 0) return [];
        if (newIndex < 0 || newIndex >= list.length) newIndex = 0;
        return list.map((item, index) => (index === newIndex ? Object.assign({}, item, { active: true }) : item));
    }

    buildCategoryDisplayList(newIndex, list) {
        if (!list || list.length === 0) return [];
        return list.map((label, index) => ({ label, active: index === newIndex }));
    }

    async changeCategoryIndex(newIndex, autoChange = true) {
        const { categories } = this.state;
        if (!categories?.length) return;
        const categoryCount = categories.length;
        const categoryFocusIndex = Math.max(0, Math.min(newIndex, categoryCount - 1));
        const nextState = { categoryFocusIndex };

        if (autoChange) {
            nextState.categoryDisplayList = this.buildCategoryDisplayList(categoryFocusIndex, categories);
        }

        this.setState(nextState);
    }

    startChannel(channel_id) {
        const { filteredItems } = this.state;
        const index = filteredItems.findIndex((item) => item.id === channel_id);
        this.changeChannelIndex(index);
        if (!this.isRadioMode()) {
            this.enterFullscreen(true);
        }
    }

    isFullscreen() {
        return getPlayerState().displayFullScreen;
    }

    changeHighlightedChannel(newIndex) {
        const { filteredItems } = this.state;
        const channelCount = filteredItems?.length || 0;
        if (channelCount === 0) return;
        let validIndex = Math.max(0, Math.min(newIndex, channelCount - 1));
        this.setState({ highlightedChannelIndex: validIndex });
    }

    async selectCategory(label) {
        const { items, selectedCategory, categoryDisplayList, categoryFocusIndex } = this.state;

        if (label === selectedCategory) {
            this.setState({ focusedSection: SECTION.CHANNEL_LIST_VERT });
            return;
        }
        if (!categoryDisplayList[categoryFocusIndex]?.active) {
            this.changeCategoryIndex(categoryFocusIndex);
        }

        const filtered = this.getFilteredItems(items, label);

        this.setState(
            {
                selectedCategory: label,
                filteredItems: filtered,
                focusedSection: SECTION.CHANNEL_LIST_VERT,
                focusIndex: -1,
            },
            () => {
                if (filtered && filtered.length > 0) {
                    this.changeChannelIndex(0);
                }
            }
        );
    }

    toggleLogos() {
        this.setState((prevState) => ({ showLogos: !prevState.showLogos }));
    }

    getActionIcon(show_logos) {
        return show_logos ? <LogoIcon /> : <LogoDisabledIcon />;
    }

    getActionButtonsMap = () => {
        const { showLogos } = this.state;
        return {
            [ACTION_ID.TOGGLE_LOGOS]: {
                id: ACTION_ID.TOGGLE_LOGOS,
                label: showLogos ? t('Channels.hide_logos') : t('Channels.show_logos'),
                Icon: this.getActionIcon(showLogos),
                onClick: () => this.toggleLogos(),
            },
        };
    };

    getFilteredItems(items = this.state.items, category = this.state.selectedCategory) {
        const modeItems = this.getModeItems(items);
        if (!category || category === ALL_CATEGORIES_LABEL) return modeItems;
        return modeItems.filter((ch) => ch.channel_type === category);
    }

    getActiveItems() {
        const { filteredItems, items } = this.state;
        if (filteredItems && filteredItems.length > 0) {
            return filteredItems;
        }
        return this.getModeItems(items);
    }

    // ---------------------------------------------------------------------------
    // Scroll helpers
    // ---------------------------------------------------------------------------

    scrollHighlightedChannelIntoView() {
        scrollChildIntoView(this.activeChannelRef.current, null, 'vertical');
    }

    scrollActiveCategoryIntoView() {
        scrollChildIntoView(this.activeCategoryRef.current, null, 'horizontal');
    }

    enterFullscreen(hideControls = false) {
        if (this.isRadioMode()) {
            return;
        }
        const { displayFullScreen } = getPlayerState();
        if (!displayFullScreen) {
            this.screenResolution();
        }
        if (hideControls) {
            this.setState({ slideList: false });
        }
    }

    async screenResolution() {
        if (this.isRadioMode()) {
            return;
        }
        return super.screenResolution();
    }

    slideShowList() {
        // Sidebar visibility is managed by slideList + bringBackControls / enterFullscreen only.
        // Base changeChannelIndex calls slideShowList and would undo an immediate hide on Enter.
    }

    toggleActions(deltaX) {
        const actionsMap = this.getActionButtonsMap();
        const actions = Object.keys(actionsMap);
        const { focusedActionId } = this.state;
        const focusedActionIndex = actions.findIndex((id) => id === focusedActionId);

        if (focusedActionIndex === -1 || actions.length === 0) return;

        // Sidebar layout: moving left from the first action returns focus to channels.
        if (focusedActionIndex === 0 && deltaX < 0) {
            this.focusSection(SECTION.CHANNEL_LIST_VERT);
            return;
        }

        const newActionIndex = (focusedActionIndex + deltaX + actions.length) % actions.length;
        const newActionId = actions[newActionIndex];

        this.setState({ focusedActionId: newActionId }, () => this.activeActionRef?.current.focus());
    }

    // ---------------------------------------------------------------------------
    // Keyboard navigation
    // ---------------------------------------------------------------------------

    focusSection(section) {
        const { focusedSection, categories, selectedCategory } = this.state;
        const leavingCategories = focusedSection === SECTION.CATEGORIES && section !== SECTION.CATEGORIES;

        if (leavingCategories) {
            const selectedIndex = Math.max(0, categories.indexOf(selectedCategory));
            const categoryDisplayList = this.buildCategoryDisplayList(selectedIndex, categories);

            this.setState({
                focusedSection: section,
                categoryFocusIndex: selectedIndex,
                categoryDisplayList,
            });
            return;
        }

        this.setState({ focusedSection: section });
    }

    bringBackControls() {
        clearTimeout(this.controlsHideTimer);
        if (!this.state.slideList) {
            this.setState({ slideList: true });
        }
        this.controlsHideTimer = setTimeout(() => {
            if (this.isFullscreen()) {
                this.setState({ slideList: false });
            }
        }, this.sleepTime);
    }

    getKeyPressCallbacks() {
        var base = super.getKeyPressCallbacks();
        var self = this;
        var callbacks = Object.assign({}, base);

        // Sidebar-only navigation — no horizontal channel-list mode.
        callbacks.onVerticalMove = function (delta) {
            self.bringBackControls();
            const { highlightedChannelIndex, filteredItems, focusedSection } = self.state;

            if (focusedSection === SECTION.CATEGORIES && delta > 0) {
                return self.focusSection(SECTION.CHANNEL_LIST_VERT);
            }

            if (focusedSection === SECTION.CHANNEL_LIST_VERT) {
                if (!filteredItems || filteredItems.length === 0) return;
                if (highlightedChannelIndex === 0 && delta < 0) {
                    return self.focusSection(SECTION.CATEGORIES);
                }
                return self.changeHighlightedChannel(highlightedChannelIndex + delta);
            }
        };

        callbacks.onHorizontalMove = function (delta) {
            self.bringBackControls();
            const { focusedSection } = self.state;

            if (focusedSection === SECTION.CATEGORIES) {
                return self.changeCategoryIndex(self.state.categoryFocusIndex + delta, false);
            }

            if (focusedSection === SECTION.CHANNEL_LIST_VERT && delta > 0) {
                return self.focusSection(SECTION.ACTION_BUTTONS);
            }

            if (focusedSection === SECTION.ACTION_BUTTONS) {
                return self.toggleActions(delta);
            }
        };

        callbacks.onBack = function () {
            return self.setState({ redirectBack: true });
        };

        callbacks.onEnter = function () {
            const { filteredItems, categories, categoryFocusIndex, highlightedChannelIndex, focusedSection } = self.state;

            if (focusedSection === SECTION.ACTION_BUTTONS) {
                // Action buttons are keyboard-focusable and self-activate on Enter.
                return;
            }

            if (focusedSection === SECTION.CATEGORIES) {
                return self.selectCategory(categories[categoryFocusIndex]);
            }

            if (focusedSection === SECTION.CHANNEL_LIST_VERT) {
                const selectedChannel = filteredItems[highlightedChannelIndex];
                if (!selectedChannel) return;
                const index = filteredItems.findIndex((item) => item.id === selectedChannel.id);
                self.setState({ slideList: false });
                self.changeChannelIndex(index);
                if (!self.isRadioMode()) {
                    self.enterFullscreen(true);
                }
            }
        };

        return callbacks;
    }
    
    getBars(BARS_TO_SHOW = 20) {
        const bars = [];
        for (let i = 0; i < BARS_TO_SHOW; i++) {
            bars.push(<span key={i} />);
        }
        return bars;
    }
   

    renderRadioPlayer() {
        const { filteredItems, focusIndex, channelLoading, channelSignalUnavailable } = this.state;
        const activeChannel = filteredItems[focusIndex];
        const title = activeChannel?.channel_id?.locale_channel?.locale_name ?? '';
        const frequency = activeChannel?.channel_id?.locale_channel?.locale_description ?? '';
        const showNowPlaying = !channelLoading && !channelSignalUnavailable;
        const bars = this.getBars(30);
        return (
            <FullScreenContainer >
                <AudioPlayerPreview>
                {showNowPlaying && (
                    <>
                        <StationCard >
                            <RadioIcon variant="outline" width={90} height={90} />
                        </StationCard>

                        <LiveBadge isActive={Boolean(activeChannel)}>{t('Radio.live')}</LiveBadge>
                        <StationTitle>{title}</StationTitle>
                        <StationFrequency>{frequency ? frequency : t('Radio.playing_now')}</StationFrequency>

                        {activeChannel && (
                            <SignalIcon height={'40px'} barCount={bars.length} width={'200px'}>
                               {bars}
                            </SignalIcon>
                        )}
                    </>
                )}
                {this.renderChannelLoadingOverlay(false)}
                </AudioPlayerPreview>
                <HiddenPlayerMount aria-hidden="true">
                    {this.getPlayerContainers()}
                </HiddenPlayerMount>
            </FullScreenContainer>
        );
    }



    // ---------------------------------------------------------------------------
    // Render helpers
    // ---------------------------------------------------------------------------

    renderVideoPlayer() {
        const playerState = getPlayerState();
        const isFullscreen = playerState.displayFullScreen;

        return (
            <ContentContainer data-tizen-content-container>
                <VideoPlayerArea>
                    {this.getPlayerContainers()}
                    {!isFullscreen && this.renderChannelLoadingOverlay(false)}
                </VideoPlayerArea>
            </ContentContainer>
        );
    }

    renderStates() {
        const { loading, showError } = this.state;

        if (loading) {
            return (
                <StreamLoadingOverlay fullscreen={true}>
                    <LoadingIndicator>
                        <LoadingSpinner>
                            <LoadingSpinnerArc />
                        </LoadingSpinner>
                        <LoadingText>{t('Channels.loading')}</LoadingText>
                    </LoadingIndicator>
                </StreamLoadingOverlay>
            );
        }

        return (
            <Container>
                <ErrorState>
                    <ErrorStateText>
                        {showError ? 'The selected language is not configured yet.' : t('Channels.channels_error')}
                    </ErrorStateText>
                </ErrorState>
            </Container>
        );
    }

    renderChannelLoadingOverlay(isFullscreen = false) {
        const { channelLoading, channelSignalUnavailable, filteredItems, focusIndex } = this.state;
        if (!channelLoading && !channelSignalUnavailable) return null;

        const channelName = filteredItems?.[focusIndex]?.channel_id?.locale_channel?.locale_name ?? '';

        return (
            <StreamLoadingOverlay fullscreen={isFullscreen}>
                <LoadingIndicator>
                    {!channelSignalUnavailable && (
                        <LoadingSpinner>
                            <LoadingSpinnerArc />
                        </LoadingSpinner>
                    )}
                    <LoadingText>
                        {channelSignalUnavailable
                            ? t('Channels.signal_unavailable')
                            : channelName}
                    </LoadingText>
                </LoadingIndicator>
            </StreamLoadingOverlay>
        );
    }

    getSidebarChannelRowState(channel, index) {
        const { focusIndex, highlightedChannelIndex, focusedSection } = this.state;
        const locale = channel?.channel_id?.locale_channel;

        return {
            isActive: channel.active || index === focusIndex,
            isHighlighted:
                highlightedChannelIndex === index && focusedSection === SECTION.CHANNEL_LIST_VERT,
            localeName: locale?.locale_name || 'Unknown Channel',
            frequency: locale?.locale_description || '',
            image: channel?.channel_id?.channel?.image_url,
        };
    }

    renderSidebarChannelList(channelList, showLogos) {
        const isRadio = this.isRadioMode();

        return channelList.map((channel, index) => {
            const { isActive, isHighlighted, localeName, frequency, image } =
                this.getSidebarChannelRowState(channel, index);

            return (
                <SidebarChannelRow
                    key={channel.id}
                    ref={isHighlighted ? this.activeChannelRef : null}
                    active={isActive}
                    highlighted={isHighlighted}
                    onClick={() => this.startChannel(channel.id)}
                >
                    {isRadio ? (
                        <>
                            <RadioCardLogoBox bgColor={channel.color}>
                                <RadioIcon variant="outline" fill="none" width={20} height={20} />
                            </RadioCardLogoBox>
                            <RadioCardMeta>
                                <RadioCardTitle>{localeName}</RadioCardTitle>
                                {frequency && <RadioCardFrequency>{frequency}</RadioCardFrequency>}
                            </RadioCardMeta>
                        </>
                    ) : (
                        <>
                            <ChannelNumber>{index}</ChannelNumber>
                            {showLogos && image && (
                                <SidebarChannelLogoBox>
                                    <CachedImg src={image} alt={localeName} />
                                </SidebarChannelLogoBox>
                            )}
                            <ChannelTitle>{localeName}</ChannelTitle>
                        </>
                    )}
                    {isActive && <SignalIcon>{this.getBars(3)}</SignalIcon>}
                </SidebarChannelRow>
            );
        });
    }






    renderCategoriesList(categories) {
        return categories.map((cat, index) => {
            const isHighlighted = this.state.categoryFocusIndex === index && this.state.focusedSection === SECTION.CATEGORIES;
            return (
                <CategoryChipButton
                    key={cat.label}
                    highlighted={isHighlighted}
                    ref={isHighlighted ? this.activeCategoryRef : null}
                    active={cat.active}
                    onClick={() => {
                        const realIndex = this.state.categories.indexOf(cat.label);
                        if (realIndex !== -1) {
                            this.changeCategoryIndex(realIndex);
                            this.selectCategory(cat.label);
                        }
                    }}
                >
                    {cat.label}
                </CategoryChipButton>
            );
        });
    }

    renderActionButtons() {
        const { focusedActionId } = this.state;
        const buttonsMap = this.getActionButtonsMap();
        return Object.values(buttonsMap).map((button) => {
            const isBtnHighligted = focusedActionId === button.id;
            return (
                <ControlPillButton key={button.id} ref={isBtnHighligted && this.activeActionRef} onClick={button.onClick}>
                    {button.Icon}
                    {button.label}
                </ControlPillButton>
            );
        });
    }

    renderNewsHeadlinesOverlay(headlines) {
        return (
            <NewsTickerContainer>
                <NewsTickerIcon>
                    <SpeakerIcon />
                </NewsTickerIcon>
                <NewsTickerViewport>
                    <NewsTickerTrack items_count={headlines.length} >
                        {/* Duplicated list ([1,2,3,1,2,3]) gives a seamless scroll loop */}
                        {[...headlines, ...headlines].map((headline, index) => (
                            <NewsTickerItem key={index}>
                                <NewsTickerText>{headline}</NewsTickerText>
                                <NewsTickerSeparator />
                            </NewsTickerItem>
                        ))}
                    </NewsTickerTrack>
                </NewsTickerViewport>
            </NewsTickerContainer>
        );
    }

    // ---------------------------------------------------------------------------
    // Main render
    // ---------------------------------------------------------------------------
    goBack() {
        this.setState({ redirectBack: true });
    }
    render() {
        if (this.state.redirectBack) {
            return <Navigate to={Route.route.LANDING} />;
        }

        const { headlines, categoryDisplayList, showLogos, filteredItems, slideList, focusedSection } = this.state;

        const playerState = getPlayerState();
        const isFullscreen = playerState.displayFullScreen;
        const mediaUrl = playerState?.url;
        const isRadio = this.isRadioMode();
        const hasChannels = filteredItems.length > 0;

        const showControls = !isFullscreen || slideList;
        const showHeadlineOverlay = headlines.length > 0 && !isRadio;
        return (
            <TvWrapper data-tizen-page-container backgroundImage={this.wallpaperImage}>


                <VideoBackground hide={!mediaUrl && !isRadio}>
                    {isRadio ? this.renderRadioPlayer() :
                     this.renderVideoPlayer()}
                </VideoBackground>

              
                {!hasChannels && this.renderStates()}

                {showControls && hasChannels && (
                    <>
                        {!isRadio && <TopRightControls focused={focusedSection === SECTION.ACTION_BUTTONS}>{this.renderActionButtons()}</TopRightControls>}

                        <SidebarPanel>
                            <NavHeader>
                                <BackIcon  >
                                <ChevronLeftIcon onClick={this.goBack.bind(this)} />
                                </BackIcon>
                                {isRadio ? <RadioIcon variant='primary' height={22} width={22} fill={"none"} /> : <TvIcon height={22} width={22} />}
                                <NavHeaderTitle>{isRadio ? t('Radio.radio') : t('Channels.channels')}</NavHeaderTitle>
                            </NavHeader>

                            <CategoryChipList focused={focusedSection === SECTION.CATEGORIES}>
                                {this.renderCategoriesList(categoryDisplayList)}
                            </CategoryChipList>

                            <SidebarChannelList
                                news_visible={headlines.length > 0}
                                focused={focusedSection === SECTION.CHANNEL_LIST_VERT}
                            >
                                {this.renderSidebarChannelList(filteredItems, showLogos)}
                            </SidebarChannelList>
                        </SidebarPanel>
                    </>
                )}

                {showHeadlineOverlay && this.renderNewsHeadlinesOverlay(headlines)}

                {isFullscreen && !isRadio && this.renderChannelLoadingOverlay(true)}
            </TvWrapper>
        );
    }
}

export default WarwickTvNews;


